import { readdirSync, readFileSync, statSync } from 'node:fs';
import { join } from 'node:path';

/**
 * ENG-5897 — regression check that surfaces sub-agent definitions whose
 * `tools:` allowlist structurally excludes every `mcp__*` MCP tool.
 *
 * Why this exists: per Anthropic's Claude Code sub-agent docs
 * (https://code.claude.com/docs/en/sub-agents) a sub-agent inherits the
 * parent's full toolset (including MCPs) when its frontmatter omits the
 * `tools:` field. The moment the field is set as an allowlist, only the
 * exact entries listed are exposed to the child. Many community-plugin
 * sub-agents ship restrictive `tools:` lists like `tools: Read, Grep`
 * with no `mcp__*` entries. When a managed agent's parent dispatches
 * one of those for multi-step tool work, the child silently reports
 * "No such tool available." for every MCP call and may fall back to
 * unsafe paths (reading raw secrets from .mcp.json — see ENG-5898).
 *
 * This module gives the manager a way to detect that class of sub-agent
 * at boot and log a structured warning, so the gap is visible at the
 * earliest possible moment rather than at first dispatch on a real
 * inbound task. Strictly read-only — no file mutation, no side effects
 * beyond `fs.readFileSync`.
 *
 * The audit deliberately does NOT fail boot — a restrictive plugin
 * sub-agent may be intentional (e.g. a read-only researcher). The
 * caller (manager-worker boot) logs findings as warnings and lets the
 * operator decide. Augmented's own `augmented-worker` is the canonical
 * antidote: it ships in `claudecode-plugin-augmented/agents/` with
 * `tools:` deliberately omitted so the parent can dispatch to it for
 * any MCP-heavy work.
 */

export interface SubagentFinding {
  /** Absolute path to the sub-agent definition that triggered the finding. */
  file: string;
  /** The `name:` declared in the sub-agent's frontmatter, or `<unknown>`. */
  name: string;
  /** Raw value of the `tools:` field as it appears in the frontmatter. */
  tools: string;
  /** One-line classification — used by the caller's log line. */
  reason: 'restrictive-tools-no-mcp';
}

interface ParsedFrontmatter {
  name?: string;
  tools?: string;
  background?: string;
}

/**
 * Pull the YAML frontmatter block from a sub-agent markdown file and
 * return the three fields we care about (`name`, `tools`, `background`)
 * as raw strings. Anything else in the frontmatter is ignored.
 *
 * Returns `null` when the file does not start with a `---` frontmatter
 * fence — those files are not Claude Code sub-agent definitions and we
 * silently skip them rather than emit a noisy parse error.
 *
 * Handles both line-ending conventions (LF and CRLF — Windows
 * contributors / cross-platform plugin authors are a real population)
 * and both `tools:` shapes Claude Code accepts:
 *
 *   tools: Read, Grep, Glob          # inline comma list (docs default)
 *
 *   tools:                            # YAML sequence (also supported,
 *     - Read                          #   GitHub issue
 *     - Grep                          #   anthropics/claude-code#19114)
 *     - mcp__augmented__*
 *
 * For the sequence form we collect items into a comma-joined string
 * shape `toolsExcludeMcp` already understands. Without this, an
 * MCP-blind sub-agent that uses the array form would slip past the
 * audit because the `tools:` line itself looks empty.
 */
function parseFrontmatter(content: string): ParsedFrontmatter | null {
  // Accept LF or CRLF line endings on the frontmatter fences.
  const match = content.match(/^---\r?\n([\s\S]*?)\r?\n---/);
  if (!match) return null;
  const out: ParsedFrontmatter = {};
  const lines = match[1]!.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    // Match `key: value` at the start of the line. We don't support
    // arbitrary multi-line YAML values, but we special-case the YAML
    // sequence form for `tools:` below because Claude Code accepts it
    // and at least one community plugin uses it.
    const m = line.match(/^(name|tools|background):\s*(.*)$/);
    if (!m) continue;
    const [, key, rawValue] = m as unknown as [string, 'name' | 'tools' | 'background', string];
    const trimmed = rawValue.trim();

    // YAML sequence for `tools:` — empty inline value followed by
    // indented `- item` lines. Collect them into a comma-joined
    // string. Any line that isn't indented `- item` (including blank
    // lines or the next top-level key) ends the sequence; the outer
    // loop's index walks past whatever we consumed.
    if (key === 'tools' && trimmed === '') {
      const items: string[] = [];
      let j = i + 1;
      while (j < lines.length) {
        const nextRaw = lines[j]!;
        const itemMatch = nextRaw.match(/^(\s+)-\s*(.+?)\s*$/);
        if (!itemMatch) break;
        // Strip surrounding quotes if the YAML author wrote them.
        items.push(itemMatch[2]!.replace(/^['"](.*)['"]$/, '$1'));
        j++;
      }
      if (items.length > 0) {
        out.tools = items.join(', ');
        i = j - 1; // resume the outer loop after the consumed items
        continue;
      }
    }

    out[key] = trimmed;
  }
  return out;
}

/**
 * True when the `tools:` frontmatter value would prevent the sub-agent
 * from receiving any `mcp__*` tool on dispatch. The allowlist is
 * considered MCP-safe if it contains either:
 *
 *   - a literal `mcp__` substring (an explicit MCP wildcard or tool
 *     name like `mcp__granola__*` or `mcp__augmented__list_tools`), or
 *   - a bare `*` / `All tools` token (the convention the docs use for
 *     full inheritance; Claude Code treats these as "everything").
 *
 * If the value is empty, the frontmatter line is effectively absent and
 * the caller falls back to the "tools: omitted" inheritance path —
 * this helper returns `false` in that case.
 */
function toolsExcludeMcp(tools: string): boolean {
  if (tools.length === 0) return false;
  if (/\bmcp__/.test(tools)) return false;
  // Anthropic's convention strings for full inheritance.
  if (/\*/.test(tools)) return false;
  if (/\bAll tools\b/i.test(tools)) return false;
  return true;
}

/**
 * Walk `dir` recursively and yield absolute paths of every `*.md` file
 * directly under an `agents/` directory. We only care about the
 * `agents/` location because that's where Claude Code looks for
 * sub-agent definitions — `skills/`, `commands/`, etc. live alongside
 * but are unrelated.
 *
 * Symlinks are followed by readdirSync's default behaviour, but we
 * cap directory depth at 6 to avoid getting stuck if a plugin
 * accidentally creates a cycle.
 */
function findSubagentFiles(dir: string, depth = 0): string[] {
  if (depth > 6) return [];
  let entries: string[];
  try {
    entries = readdirSync(dir);
  } catch {
    return []; // Directory missing or unreadable — silently skip.
  }
  const out: string[] = [];
  const isAgentsDir = dir.endsWith('/agents') || dir.endsWith('\\agents');
  for (const entry of entries) {
    const full = join(dir, entry);
    let s: ReturnType<typeof statSync>;
    try {
      s = statSync(full);
    } catch {
      continue;
    }
    if (s.isDirectory()) {
      out.push(...findSubagentFiles(full, depth + 1));
    } else if (s.isFile() && isAgentsDir && entry.endsWith('.md')) {
      out.push(full);
    }
  }
  return out;
}

/**
 * Scan the given root directories for sub-agent definitions whose
 * `tools:` allowlist would block every `mcp__*` tool. The caller
 * passes paths like `~/.claude/agents`, `~/.claude/plugins`, and the
 * agent's project `.claude` dir; we walk each and filter by the
 * `agents/` location convention.
 */
export function auditSubagentMcpBindings(rootDirs: string[]): SubagentFinding[] {
  const findings: SubagentFinding[] = [];
  const seen = new Set<string>();
  for (const root of rootDirs) {
    for (const file of findSubagentFiles(root)) {
      if (seen.has(file)) continue;
      seen.add(file);
      let content: string;
      try {
        content = readFileSync(file, 'utf-8');
      } catch {
        continue;
      }
      const fm = parseFrontmatter(content);
      if (!fm || typeof fm.tools !== 'string') continue;
      if (!toolsExcludeMcp(fm.tools)) continue;
      findings.push({
        file,
        name: fm.name ?? '<unknown>',
        tools: fm.tools,
        reason: 'restrictive-tools-no-mcp',
      });
    }
  }
  return findings;
}

/**
 * Format an audit finding as a single-line structured log entry that
 * matches the manager-worker stderr convention
 * (`[manager-worker] [<subsystem>] ...`). The caller picks where to
 * emit (stderr / manager.log / both).
 */
export function formatFinding(f: SubagentFinding): string {
  // Quote the tools value so a comma-bearing list doesn't confuse a
  // future log-parser that splits on whitespace.
  return `[manager-worker] [subagent-mcp-audit] file=${f.file} name=${f.name} reason=${f.reason} tools=${JSON.stringify(f.tools)}`;
}
